import React, { useState, useContext, useEffect } from 'react';
import SelectWrite from '../components/SelectWrite';
import SelectWriteCategory from '../components/SelectCategory';
import ManageGroupStudents from '../components/ManageGroupStudents';
import AddStudentsToGroup from '../components/AddStudentsToGroup';

import { AuthContext } from '../../../services/auth/AuthProvider';
import { itemDateColor } from '../../../services/utils';
import { ArrowLeft, Users } from 'react-feather';
import { Link, Router, useNavigate, useLocation } from '@reach/router';
import styles from '../Writes.module.scss';
import { Search } from 'react-feather';
import ConfigureBookTemplates from '../components/ConfigureBookTemplates';
import ManageGroups from '../components/ManageGroups';
import { GET_GROUPS } from '../services/graphql';
import { useQuery } from '@apollo/react-hooks';

const StartWrite = () => {
    const { currentUser: teacher } = useContext(AuthContext);
    const [selectedClass, setSelectedClass] = useState(null);
    const [searchThermClasses, setSearchThermClasses] = useState('');
    const [visibleClasses, setVisibleClasses] = useState([]);

    const [selectedGroup, setSelectedGroup] = useState(null);
    const [searchThermGroups, setSearchThermGroups] = useState('');
    const [visibleGroups, setVisibleGroups] = useState([]);

    const [selectedWriteType, setSelectedWriteType] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    const { data } = useQuery(GET_GROUPS, {
        variables: {
            schoolId: teacher.school.id,
        },
    });

    // Effects
    useEffect(() => {
        const studentClassesOfTeacher = teacher.studentsClasses;
        const result =
            searchThermClasses === ''
                ? studentClassesOfTeacher
                : studentClassesOfTeacher.filter((currentClass) =>
                      currentClass?.name
                          ?.toLowerCase()
                          .includes(searchThermClasses)
                  );
        setVisibleClasses(result);
    }, [searchThermClasses]);

    useEffect(() => {
        const results = searchThermGroups
            ? visibleGroups.filter((visibleGroup) =>
                  visibleGroup.name
                      .toLowerCase()
                      .includes(searchThermGroups.toLowerCase())
              )
            : data?.getGroups
            ? data.getGroups
            : [];
        setVisibleGroups(results);
        if (selectedGroup) {
            const selectedGroupUpdated = results.filter(
                (res) => res.id === selectedGroup.id
            );
            if (selectedGroupUpdated) {
                setSelectedGroup(selectedGroupUpdated[0]);
            }
        }
    }, [searchThermGroups, data]);

    useEffect(() => {
        // In case of deeplinking, compute selected class by the id in the url.
        const splitPath = location.pathname.split('/');
        if (splitPath.length > 3 && !selectedClass && !selectedGroup) {
            const idFromUrl = splitPath[3];
            const classById = teacher.studentsClasses.find(
                (studentClass) => studentClass.id === idFromUrl
            );
            if (classById) {
                setSelectedClass(classById);
            }

            if (data) {
                const selectedGroupById = data.getGroups.find(
                    (group) => group.id === idFromUrl
                );
                if (selectedGroupById) {
                    setSelectedGroup(selectedGroupById);
                }
            }
        }
    }, [teacher, data]);

    return (
        <>
            <Router>
                <StartWriteContent
                    path='/'
                    default
                    setSelectedClass={setSelectedClass}
                    setSelectedGroup={setSelectedGroup}
                    searchThermGroups={searchThermGroups}
                    searchThermClasses={searchThermClasses}
                    setSearchThermGroups={setSearchThermGroups}
                    setSearchThermClasses={setSearchThermClasses}
                    visibleClasses={visibleClasses}
                    visibleGroups={visibleGroups}
                    navigate={navigate}
                />

                <ManageGroups
                    path='/manageGroups'
                    availableGroups={data}
                    setSelectedGroup={setSelectedGroup}
                    navigateBack={() => navigate('/startWrite')}
                    schoolId={teacher.school.id}
                ></ManageGroups>

                <ManageGroupStudents
                    path='manageGroupStudents/:groupId'
                    selectedGroup={selectedGroup}
                    schoolId={teacher.school.id}
                    navigateBack={() => {
                        setSelectedGroup(null);
                        navigate('/startWrite/manageGroups');
                    }}
                ></ManageGroupStudents>

                <AddStudentsToGroup
                    path='addStudentsToGroup/:groupId'
                    teacherStudentClasses={teacher.studentsClasses}
                    schoolId={teacher.school.id}
                    selectedGroup={selectedGroup}
                    navigateBack={() => {
                        navigate(
                            selectedGroup
                                ? `/startWrite/manageGroupStudents/${selectedGroup.id}`
                                : '/startWrite/manageGroups'
                        );
                    }}
                ></AddStudentsToGroup>

                <SelectWrite
                    path='selectWrite/:classId'
                    selectedClass={selectedClass}
                    selectedGroup={selectedGroup}
                    setSelectedWriteType={setSelectedWriteType}
                    navigateBack={() => {
                        setSelectedClass(null);
                        navigate('/startWrite');
                    }}
                />

                <SelectWriteCategory
                    path='selectCategory/:classId'
                    selectedClass={selectedClass}
                    selectedGroup={selectedGroup}
                    navigateBack={() => {
                        setSelectedCategory(null);
                        navigate(
                            `/startWrite/selectWrite/${
                                selectedClass
                                    ? selectedClass.id
                                    : selectedGroup.id
                            }`
                        );
                    }}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    setSelectedSubCategory={setSelectedSubCategory}
                />

                <ConfigureBookTemplates
                    path='configureWriteChaptersIsp/:classId'
                    navigateBack={() => {
                        navigate(
                            `/startWrite/selectCategory/${
                                selectedClass
                                    ? selectedClass.id
                                    : selectedGroup.id
                            }`
                        );
                    }}
                    selectedClass={selectedClass}
                    selectedGroup={selectedGroup}
                    selectedCategory={selectedCategory}
                    selectedSubcategory={selectedSubCategory}
                />
            </Router>
        </>
    );
};

export default StartWrite;

function StartWriteContent({
    teacher,
    visibleClasses,
    setSelectedClass,
    navigate,
    searchThermClasses,
    setSearchThermClasses,
    setSearchThermGroups,
    searchThermGroups,
    visibleGroups,
    setSelectedGroup,
}) {
    return (
        <>
            <div className='page-header'>
                <div className='page-header__content'>
                    <div className='page-header__content__inner'>
                        <h2 className='page-header__content__title'>
                            <Users /> Select a class/group{' '}
                        </h2>
                    </div>
                </div>
                <div className='page-header__actions'>
                    <Link
                        className='u-display-flex u-align-center u-text-dark'
                        to='/'
                    >
                        <ArrowLeft className='u-m-right-1' /> Back
                    </Link>
                </div>
            </div>
            {teacher?.studentsClasses.length === 0 ? (
                <p>No classes assigned yet</p>
            ) : (
                <>
                    <div className={styles.selectAClass}>
                        <h2 className={styles.selectAClass}>Select a class</h2>

                        <div className='col-md-4'>
                            <div className={styles.utilitiesSearch}>
                                <input
                                    className={`${styles.utilitiesBtn} basic-form__text-box u-align-center`}
                                    type='search'
                                    placeholder='Search'
                                    onChange={(e) =>
                                        setSearchThermClasses(e.target.value)
                                    }
                                    value={searchThermClasses}
                                />
                                {!searchThermClasses && (
                                    <Search
                                        size={20}
                                        className={styles.utilitiesSearchIcon}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className='row'>
                        {visibleClasses && visibleClasses.length ? (
                            visibleClasses.map((cls) => (
                                <button
                                    key={cls.id}
                                    onClick={() => {
                                        setSelectedClass(cls);
                                        navigate(
                                            `/startWrite/selectWrite/${cls.id}`
                                        );
                                    }}
                                    className={`${styles.write} col-lg-4 col-md-6`}
                                >
                                    <div className='card card-hover'>
                                        <div
                                            className={styles.writeContent}
                                            style={{
                                                background: itemDateColor(
                                                    cls.createdAt
                                                ),
                                            }}
                                        >
                                            <h2 className={styles.writeTitle}>
                                                {cls.name}
                                            </h2>
                                        </div>
                                        <div className='card-footer'>
                                            <p className={styles.writeSubTitle}>
                                                {cls.description}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            ))
                        ) : (
                            <p>No available classes.</p>
                        )}
                    </div>
                </>
            )}

            <>
                <div className={styles.selectAClass}>
                    <h2 className={styles.selectAClass}>Select a group</h2>
                    <p
                        onClick={() => {
                            navigate('/startWrite/manageGroups/');
                        }}
                        className={styles.manageGroups}
                    >
                        Manage Groups
                    </p>
                    <div className='col-md-4'>
                        <div className={styles.utilitiesSearch}>
                            <input
                                className={`${styles.utilitiesBtn} basic-form__text-box u-align-center`}
                                type='search'
                                placeholder='Search'
                                onChange={(e) =>
                                    setSearchThermGroups(e.target.value)
                                }
                                value={searchThermGroups}
                            />

                            {!searchThermGroups && (
                                <Search
                                    size={20}
                                    className={styles.utilitiesSearchIcon}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className='row'>
                    {visibleGroups && visibleGroups.length ? (
                        visibleGroups.map((group) => (
                            <button
                                key={group.id}
                                onClick={() => {
                                    setSelectedGroup(group);
                                    navigate(
                                        `/startWrite/selectWrite/${group.id}`
                                    );
                                }}
                                className={`${styles.write} col-lg-4 col-md-6`}
                            >
                                <div className='card card-hover'>
                                    <div
                                        className={styles.writeContent}
                                        style={{
                                            background: itemDateColor(
                                                group.createdAt
                                            ),
                                        }}
                                    >
                                        <h2 className={styles.writeTitle}>
                                            {group.name}
                                        </h2>
                                    </div>
                                    <div className='card-footer'>
                                        <p className={styles.writeSubTitle}>
                                            {group.description}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        ))
                    ) : (
                        <p>No available groups.</p>
                    )}
                </div>
            </>
        </>
    );
}
